
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Camera, Wand2 } from "lucide-react";
import { supabase } from "@/lib/supabase";

interface UrlInputProps {
  url: string;
  setUrl: (url: string) => void;
  onSubmit: (input: string, type: "url" | "text") => void;
  isLoading: boolean;
}

export const UrlInput = ({ url, setUrl, onSubmit, isLoading }: UrlInputProps) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error("Please enter a recipe URL");
      return;
    }
    if (!url.startsWith("http")) {
      toast.error("Please enter a valid URL");
      return;
    }

    onSubmit(url, "url");
  };

  const handleFileUpload = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setIsProcessingFile(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        formData.append('userId', user.id);
      }

      const { data, error } = await supabase.functions.invoke('process-recipe-file', {
        body: formData,
      });

      if (error) {
        throw error;
      }

      if (data) {
        const recipeText = `Title: ${data.title}\nDescription: ${data.description}\nIngredients:\n${
          data.ingredients.map(ing => `- ${ing.amount} ${ing.unit} ${ing.name}`).join('\n')
        }\nSteps:\n${
          data.steps.map((step, i) => `${i + 1}. ${step.instruction}`).join('\n')
        }`;

        onSubmit(recipeText, "text");
      }
    } catch (error) {
      console.error('File processing error:', error);
      toast.error("Failed to process the file. Please try again.");
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleCameraCapture = async () => {
    if (!isMobile) {
      // For desktop testing, we'll use a regular file input
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      
      fileInput.addEventListener('change', async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          await handleFileUpload(file);
        }
      });

      fileInput.click();
      return;
    }

    try {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.capture = 'environment';
      
      fileInput.addEventListener('change', async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          await handleFileUpload(file);
        }
      });

      fileInput.click();
    } catch (error) {
      console.error('Camera error:', error);
      toast.error("Unable to access camera. Please check your permissions.");
    }
  };

  return (
    <form onSubmit={handleUrlSubmit} className="w-full">
      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Input
            type="url"
            placeholder={isMobile ? "www..." : "E.g., www.bestrecipes.com/chicken-parm"}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full h-16 px-6 pr-[140px] text-lg bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 transition-all duration-200 shadow-sm hover:shadow-md overflow-hidden text-ellipsis"
          />
          <div className="absolute inset-y-0 right-0 flex gap-2 items-center pr-2 pl-8 bg-gradient-to-l from-white via-white to-transparent">
            <Button
              type="button"
              variant="outline"
              onClick={handleCameraCapture}
              className="h-12 hover:bg-gray-100 transition-colors"
              aria-label={isMobile ? "Take Photo" : "Upload Photo"}
              disabled={isProcessingFile}
            >
              <Camera className="h-5 w-5" />
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="h-12 w-full bg-primary hover:bg-primary/90 text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg shadow-sm group"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Converting...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Wand2 className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span className="text-lg">Convert</span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
